import { type Cookies } from '@sveltejs/kit';
import { browser } from '$app/environment';
import { goto } from '$app/navigation';
import { hashEmail } from '$lib';
import { Mutex } from '$lib/mutex';
import { InvalidTokenError, jwtDecode, type JwtPayload } from 'jwt-decode';

const LOCALE_STORAGE_KEY_USERDATA = 'user';

export default class AuthProvider {
	accessToken = $state<string>();
	userData = $state<UserData | null>(this.#loadUserDataFromLocalStorage());

	#loadUserDataFromLocalStorage(): UserData | null {
		if (browser) {
			const lsItem = localStorage?.getItem(LOCALE_STORAGE_KEY_USERDATA);
			if (lsItem) {
				return JSON.parse(lsItem);
			}
		}
		return null;
	}

	#data = $derived.by(() => {
		if (this.accessToken) {
			try {
				return jwtDecode<JwtPayload>(this.accessToken);
			} catch (err) {
				if (err instanceof InvalidTokenError) return null;
				throw err;
			}
		}
	});

	async refreshToken() {
		const refreshedResponse = await fetch('/customer/account/refresh', {
			method: 'POST',
		});

		if (refreshedResponse.status !== 200) {
			this.accessToken = undefined;
			throw new Error('refresh token expired');
		}

		this.accessToken = ((await refreshedResponse.json()) as { access_token: string }).access_token;
	}

	async fetch(url: string, options: RequestInit = {}): Promise<Response> {
		return fetch(url, {
			...options,
			headers: {
				...options.headers,
				Authorization: `Bearer ${await this.#token()}`,
			},
		});
	}

	async logout() {
		if (!this.accessToken) return;

		const formData = new FormData();
		formData.set('access_token', this.accessToken);

		await fetch('/customer/account/logout', {
			method: 'POST',
			body: formData,
		});

		// workaround to delete cookies, by default Firefox delete cookies from storage but not in document.cookie
		// when not httpOnly is false
		document.cookie = 'refresh_token_expired_at=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

		this.accessToken = undefined;

		await goto('/customer/account/login?redirect=/customer/order');
	}

	isAuthenticated = $derived.by(() => {
		void this.accessToken; // observe (for logout)

		if (!browser) {
			return false;
		}

		const cookie = document.cookie.match(/refresh_token_expired_at=(\d+)/);
		if (!cookie) {
			return false;
		}
		return !isExpired(cookie[1]);
	});

	#mutex = new Mutex();
	async #token(): Promise<string> {
		try {
			await this.#mutex.acquire();
			if (this.accessToken && !isExpired(this.#data?.exp)) {
				return this.accessToken as string;
			}

			await this.refreshToken();
			return this.accessToken as string;
		} catch {
			await goto(`/customer/account/login?redirect=${window.location.pathname}`);
			// unreachable (throw to ensure function signature)
			throw new Error('refresh token expired');
		} finally {
			this.#mutex.release();
		}
	}

	async success(accessToken: string | unknown, email: string | unknown) {
		this.accessToken = accessToken as string;
		if (this.customerId) {
			this.#saveUserDataToLocalStorage({
				id: this.customerId,
				hashedEmail: await hashEmail(email as string),
			});
		}
	}

	#saveUserDataToLocalStorage(newUserData: UserData) {
		if (browser) {
			localStorage?.setItem(LOCALE_STORAGE_KEY_USERDATA, JSON.stringify(newUserData));
			this.userData = newUserData;
		}
	}

	get customerId() {
		return this.#data?.sub;
	}
}

export function isExpired(expiredAt: string | number | undefined) {
	return expiredAt && Number(expiredAt) - 30 <= Date.now() / 1000;
}

export function isAuthenticatedFromRequest(cookies: Cookies) {
	const expiredAt = cookies.get('refresh_token_expired_at');
	return expiredAt && !isExpired(expiredAt);
}

export async function setCookiesFromResponse(response: Response, cookies: Cookies) {
	const data: { access_token: string; refresh_token: string; refresh_token_expired_at: string } =
		await response.json();

	if (!response.ok) {
		removeCookies(cookies);
		throw new Error('failed to refresh token');
	}

	cookies.set('refresh_token', data.refresh_token, { path: '/' });
	cookies.set(
		'refresh_token_expired_at',
		(new Date(data.refresh_token_expired_at).getTime() / 1000).toString(),
		{
			path: '/',
			httpOnly: false,
		},
	);

	return {
		access_token: data.access_token,
	} as { access_token: string };
}

export function removeCookies(cookies: Cookies) {
	cookies.delete('refresh_token', { path: '/' });
	cookies.delete('refresh_token_expired_at', { path: '/' });
}
